import React, { useState, useEffect, useMemo } from 'react';
import EditableDataTable from '../EditableDataTable/EditableDataTable';
import { Building2, Package, FolderOpen, BarChart3, Target, RefreshCw, Plus, X, Sparkles, Zap, Activity, ArrowLeft, ChevronRight } from 'lucide-react';
import apiClient from '../../lib/apiClient';

function Products({ customerId }) {
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showLinkCapabilities, setShowLinkCapabilities] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [compareProducts, setCompareProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vendors');

  // Common vendors for California state contracts
  const commonVendors = [
    'Cisco', 'Microsoft', 'VMware', 'Palo Alto Networks', 'Fortinet', 
    'Splunk', 'CrowdStrike', 'Zscaler', 'ServiceNow', 'Salesforce',
    'Oracle', 'IBM', 'Dell', 'HPE', 'NetApp', 'Citrix', 'F5 Networks',
    'Juniper Networks', 'Aruba Networks', 'SolarWinds', 'Datadog',
    'New Relic', 'Elastic', 'MongoDB', 'Redis Labs', 'Snowflake'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vendorsData, productsData, categoriesData, capabilitiesData] = await Promise.all([
        apiClient.get('/vendors'),
        apiClient.get('/products'),
        apiClient.get('/categories'),
        apiClient.get('/capabilities')
      ]);

      setVendors(vendorsData);
      setProducts(productsData);
      setCategories(categoriesData);
      setCapabilities(capabilitiesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoize product counts for better performance
  const productCountsByVendor = useMemo(() => {
    const counts = {};
    products.forEach(product => {
      if (product.vendor_id) {
        counts[product.vendor_id] = (counts[product.vendor_id] || 0) + 1;
      }
    });
    return counts;
  }, [products]);

  // Enhance vendors data with product counts
  const vendorsWithCounts = useMemo(() => {
    return vendors.map(vendor => ({
      ...vendor,
      product_count: productCountsByVendor[vendor.vendor_id] || 0
    }));
  }, [vendors, productCountsByVendor]);

  // Table columns configuration
  const productColumns = [
    { key: 'product_name', label: 'Product Name', required: true },
    { key: 'vendor_name', label: 'Vendor', required: true },
    { key: 'description', label: 'Description' },
    { key: 'list_price', label: 'List Price', format: (value) => value ? `${value}` : '' },
    { key: 'licensing_model', label: 'Licensing' },
    { 
      key: 'categories', 
      label: 'Categories',
      format: (value) => {
        if (!value || !Array.isArray(value)) return '';
        return value.map(cat => `${cat.icon || ''} ${cat.category_name}`).join(', ');
      }
    },
    {
      key: 'capability_count',
      label: 'Capabilities',
      format: (value) => value || 0
    }
  ];

  const vendorColumns = [
    { key: 'vendor_name', label: 'Vendor Name', required: true },
    { 
      key: 'product_count', 
      label: 'Products',
      format: (value) => value || 0
    }
  ];

  const categoryColumns = [
    { key: 'category_name', label: 'Category Name', required: true },
    { key: 'category_code', label: 'Code', required: true },
    { key: 'icon', label: 'Icon' },
    { key: 'description', label: 'Description' },
    { 
      key: 'product_count', 
      label: 'Products',
      format: (value) => value || 0
    }
  ];

  // Get products for selected vendor
  const getVendorProducts = () => {
    if (!selectedVendor) return products;
    return products.filter(p => p.vendor_id === selectedVendor.vendor_id);
  };

  // Get products for selected category
  const getCategoryProducts = () => {
    if (!selectedCategory) return products;
    return products.filter(p => 
      p.categories && p.categories.some(c => c.category_id === selectedCategory.category_id)
    );
  };

  // Add new vendor
  const handleAddVendor = async (vendorData) => {
    try {
      await apiClient.post('/vendors', vendorData);
      await fetchData();
      return true;
    } catch (error) {
      console.error('Error adding vendor:', error);
      throw error;
    }
  };

  // Add new category
  const handleAddCategory = async (categoryData) => {
    try {
      await apiClient.post('/categories', categoryData);
      await fetchData();
      setShowAddCategory(false);
      return true;
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  };

  // Add new product
  const handleAddProduct = async (productData) => {
    try {
      const newProduct = await apiClient.post('/products', {
        ...productData,
        vendor_id: selectedVendor?.vendor_id || productData.vendor_id
      });

      // Assign categories if selected
      if (productData.category_ids && productData.category_ids.length > 0) {
        await apiClient.post(`/products/${newProduct.product_id}/categories`, {
          category_ids: productData.category_ids,
          primary_category_id: productData.category_ids[0]
        });
      }

      await fetchData();
      return true;
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  };

  // Update product
  const handleUpdateProduct = async (productId, updates) => {
    try {
      await apiClient.put(`/products/${productId}`, updates);
      await fetchData();
      return true;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    try {
      await apiClient.del(`/products/${productId}`);
      await fetchData();
      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  // Update vendor
  const handleUpdateVendor = async (vendorId, updates) => {
    try {
      await apiClient.put(`/vendors/${vendorId}`, updates);
      await fetchData();
      return true;
    } catch (error) {
      console.error('Error updating vendor:', error);
      throw error;
    }
  };

  // Delete vendor
  const handleDeleteVendor = async (vendorId) => {
    try {
      await apiClient.del(`/vendors/${vendorId}`);
      await fetchData();
      return true;
    } catch (error) {
      console.error('Error deleting vendor:', error);
      throw error;
    }
  };

  // Update category
  const handleUpdateCategory = async (categoryId, updates) => {
    try {
      await apiClient.put(`/categories/${categoryId}`, updates);
      await fetchData();
      return true;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  };

  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    try {
      await apiClient.del(`/categories/${categoryId}`);
      await fetchData();
      return true;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-black"></div>
        </div>
        <p className="text-gray-600">Loading product database...</p>
      </div>
    );
  }

  const tabs = [
    { id: 'vendors', label: 'Vendors', icon: Building2 },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'categories', label: 'Categories', icon: FolderOpen },
    { id: 'comparison', label: 'Compare', icon: BarChart3, badge: compareProducts.length || null },
    { id: 'analysis', label: 'Gap Analysis', icon: Target }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Product Intelligence Hub</h1>
                <p className="text-gray-500 mt-1">Manage vendors, products, and capabilities</p>
              </div>
              <button 
                onClick={fetchData}
                className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl">
                    <Building2 className="w-6 h-6 text-indigo-600" />
                  </div>
                  <Activity className="w-4 h-4 text-gray-400" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">{vendors.length}</h3>
                <p className="text-sm text-gray-600 font-medium mt-1">Vendors</p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl">
                    <Package className="w-6 h-6 text-violet-600" />
                  </div>
                  <Sparkles className="w-4 h-4 text-violet-400" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">{products.length}</h3>
                <p className="text-sm text-gray-600 font-medium mt-1">Products</p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl">
                    <FolderOpen className="w-6 h-6 text-emerald-600" />
                  </div>
                  <Zap className="w-4 h-4 text-emerald-400" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">{categories.length}</h3>
                <p className="text-sm text-gray-600 font-medium mt-1">Categories</p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl">
                    <Target className="w-6 h-6 text-amber-600" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-amber-400" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">{capabilities.length}</h3>
                <p className="text-sm text-gray-600 font-medium mt-1">Capabilities</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative py-4 px-1 flex items-center gap-2 text-sm font-medium transition-all duration-200
                    ${activeTab === tab.id 
                      ? 'text-black border-b-2 border-black' 
                      : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.badge && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-black text-white rounded-full">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'vendors' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <EditableDataTable
              data={vendorsWithCounts}
              columns={vendorColumns}
              idField="vendor_id"
              title="Vendors"
              onUpdate={handleUpdateVendor}
              onDelete={handleDeleteVendor}
              onAdd={handleAddVendor}
              searchPlaceholder="Search vendors..."
              addButtonText="Add Vendor"
              enableEdit={true}
              enableSort={true}
              enableFilter={true}
              enableColumnSelector={true}
              enableDelete={true}
              enableAdd={true}
              enableSearch={true}
              enableLock={true}
              nonEditableColumns={['product_count']}
            />
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              <EditableDataTable
                data={selectedVendor ? getVendorProducts() : products}
                columns={productColumns}
                idField="product_id"
                title={selectedVendor ? `Products for ${selectedVendor.vendor_name}` : "All Products"}
                onUpdate={handleUpdateProduct}
                onDelete={handleDeleteProduct}
                onAdd={handleAddProduct}
                searchPlaceholder="Search products..."
                addButtonText="Add Product"
                enableEdit={true}
                enableSort={true}
                enableFilter={true}
                enableColumnSelector={true}
                enableDelete={true}
                enableAdd={true}
                enableSearch={true}
                enableLock={true}
                nonEditableColumns={['vendor_name', 'categories', 'capability_count']}
                defaultVisibleColumns={['product_name', 'vendor_name', 'description', 'list_price', 'licensing_model', 'categories']}
              />
            </div>
            {selectedVendor && (
              <button 
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200"
                onClick={() => setSelectedVendor(null)}
              >
                <ArrowLeft className="w-4 h-4" />
                Show All Products
              </button>
            )}
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              <EditableDataTable
                data={categories}
                columns={categoryColumns}
                idField="category_id"
                title="Categories"
                onUpdate={handleUpdateCategory}
                onDelete={handleDeleteCategory}
                onAdd={handleAddCategory}
                searchPlaceholder="Search categories..."
                addButtonText="Add Category"
                enableEdit={true}
                enableSort={true}
                enableFilter={true}
                enableColumnSelector={true}
                enableDelete={true}
                enableAdd={true}
                enableSearch={true}
                enableLock={true}
                nonEditableColumns={['product_count']}
              />
            </div>
            {selectedCategory && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Products in {selectedCategory.category_name}
                    </h3>
                  </div>
                  <EditableDataTable
                    data={getCategoryProducts()}
                    columns={productColumns}
                    idField="product_id"
                    title=""
                    onUpdate={handleUpdateProduct}
                    onDelete={handleDeleteProduct}
                    onAdd={handleAddProduct}
                    searchPlaceholder="Search products..."
                    addButtonText="Add Product"
                    enableEdit={true}
                    enableSort={true}
                    enableFilter={false}
                    enableColumnSelector={false}
                    enableDelete={true}
                    enableAdd={false}
                    enableSearch={true}
                    enableLock={true}
                    compactMode={true}
                    nonEditableColumns={['vendor_name', 'categories', 'capability_count', 'created_at']}
                  />
                </div>
                <button 
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200"
                  onClick={() => setSelectedCategory(null)}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Categories
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'comparison' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Product Comparison</h3>
              <p className="text-gray-600 mb-8">Select products from the Products tab to compare their capabilities</p>
            </div>
            {compareProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex p-6 bg-gray-100 rounded-full mb-6">
                  <BarChart3 className="w-12 h-12 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-6">No products selected for comparison</p>
                <button 
                  className="px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-all duration-200"
                  onClick={() => setActiveTab('products')}
                >
                  Go to Products
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-700 font-medium">Selected products: {compareProducts.length}</p>
                {/* Comparison implementation here */}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analysis' && customerId && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-2xl font-semibold text-gray-900">Gap Analysis</h3>
              <button className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh Analysis
              </button>
            </div>
            <div className="text-center py-12">
              <div className="inline-flex p-6 bg-gray-100 rounded-full mb-6">
                <Target className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-500">Gap analysis visualization coming soon...</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Vendor Modal */}
      {showAddVendor && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowAddVendor(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Add new vendor</h3>
              <button
                onClick={() => setShowAddVendor(false)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Name
                </label>
                <input 
                  type="text" 
                  id="vendorNameInput"
                  placeholder="e.g., Cisco, Microsoft, VMware"
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all duration-200"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const input = document.getElementById('vendorNameInput');
                      if (input.value) {
                        handleAddVendor({ vendor_name: input.value });
                        setShowAddVendor(false);
                      }
                    }
                  }}
                />
              </div>
              
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-3">Quick add common vendors:</p>
                <div className="flex flex-wrap gap-2">
                  {commonVendors
                    .filter(v => !vendors.some(existing => existing.vendor_name === v))
                    .slice(0, 6)
                    .map(vendor => (
                      <button
                        key={vendor}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                        onClick={() => {
                          handleAddVendor({ vendor_name: vendor });
                          setShowAddVendor(false);
                        }}
                      >
                        {vendor}
                      </button>
                    ))}
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button 
                  onClick={() => setShowAddVendor(false)}
                  className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    const input = document.getElementById('vendorNameInput');
                    if (input.value) {
                      handleAddVendor({ vendor_name: input.value });
                      setShowAddVendor(false);
                    }
                  }}
                  className="px-5 py-2.5 bg-black text-white rounded-xl hover:bg-gray-800 transition-all duration-200"
                >
                  Add Vendor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;